import { InsiIdentityStatusIds, AttendingPhysicianStatusIds } from '@toktokdoctech/tokapi-client'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CloseIcon from '@mui/icons-material/Close'
import { IconButton, InputAdornment, TextField, Typography } from '@mui/material'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import { useDebouncedCallback } from '@workspace/frontend-kit'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import FormLabel from '@mui/material/FormLabel'
import Hidden from '@mui/material/Hidden'
import Menu from '@mui/material/Menu'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import makeStyles from '@mui/styles/makeStyles'
import { frFR } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import clsx from 'clsx'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { useState } from 'react'
import { useFormsStyles } from '../../../styles'
import useButtonStyles from '../../../styles/buttonsStyles'
import styles from '../../Common/_styles/filterListStyles'
import listStyles from '../../Common/_styles/containerListStyles'
import { attendingPhysicianStatusOptions, PatientFilters, PatientListFiltersProps } from './_props'
import { runSsnValidation } from '../../../utils/validators'
import InformationalTooltip from '../../Common/InformationalTooltip'

dayjs.extend(utc)

// @ts-expect-error TS(2345) FIXME: Argument of type '(theme: any) => { root: { flexGr... Remove this comment to see the full error message
const useStyles = makeStyles(styles)
// @ts-expect-error TS(2345) FIXME: Argument of type '(theme: any) => { root: { flexGr... Remove this comment to see the full error message
const useListStyles = makeStyles(listStyles)

const PatientListFilters = ({ onChange, onMessage, filters, isWorkbench }: PatientListFiltersProps) => {
    const classes = useStyles()
    const listClasses = useListStyles()
    const buttonClasses = useButtonStyles()
    const formsClasses = useFormsStyles()

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const insiIdentityStatuses: InsiIdentityStatusIds[] = ['Provisional', 'Recovered', 'Validated', 'Qualified']
    const menuOpen = Boolean(anchorEl)
    const hasFilterActive = Object.values(filters).some((filter) => !!filter)
    const [searchInput, setSearchInput] = useState<string>('')

    const getInsiIdentityStatusLabel = (status: InsiIdentityStatusIds): string => {
        if (status === 'Provisional') return 'Provisoire'
        if (status === 'Recovered') return 'Récupérée'
        if (status === 'Validated') return 'Validée'
        if (status === 'Qualified') return 'Qualifiée'
        throw new Error(`[Patients Filters] - unmanaged InsiIdentity status: ${status}`)
    }

    const handleSearch = (value: string) => {
        if (Number(value.charAt(0))) {
            const matches = value.match(/([à-üÀ-Ü])|([¬!"£$%^&*()_+`\-={}:@~<>?[\];'#,./\\| ])/g)
            if (matches) {
                onMessage({
                    text: 'Le matricule INS est invalide, les caractères spéciaux ou espaces ne sont pas acceptés',
                    level: 'error',
                })
                return
            }

            const validation = runSsnValidation({ ssn: value, fromSearch: true })
            if (typeof validation === 'string') {
                onMessage({ text: validation, level: 'error' })
                return
            }
        }

        onMessage(null)
        handleFiltersChange({ search: value })
    }

    const debounceSearch = useDebouncedCallback((value: string) => handleSearch(value), 300)

    const handleInsiStatusChange = (value: InsiIdentityStatusIds) => {
        const newInsi = filters.insiIdentityStatuses === value ? undefined : value
        handleFiltersChange({ insiIdentityStatuses: newInsi })
    }

    const handleAttendingPhysicianStatusChange = (value: AttendingPhysicianStatusIds) => {
        const newReferringStatus = filters.attendingPhysicianStatus === value ? undefined : value
        handleFiltersChange({ attendingPhysicianStatus: newReferringStatus })
    }

    const handleBirthDateChange = (date: Dayjs | null) => {
        if (!date || !date.isValid()) return
        if (date.year() < 1900) return

        const utcDate = dayjs
            .utc(
                `${date.year()}-${(date.month() + 1).toString().padStart(2, '0')}-${date
                    .date()
                    .toString()
                    .padStart(2, '0')}`,
            )
            .toISOString()
        handleFiltersChange({ birthDate: utcDate })
    }

    const handleFiltersChange = (updatedFilter: PatientFilters) => {
        const merged: Partial<PatientFilters> = { ...filters, ...updatedFilter }

        const cleaned = Object.fromEntries(
            Object.entries(merged).filter(([, value]) => Boolean(value)),
        ) as Partial<PatientFilters>

        console.log(`[handlefilterschange] ${JSON.stringify(cleaned, null, 2)}`)
        onChange(cleaned)
    }

    const handleResetFilter = () => {
        onChange({})
        setSearchInput('')
    }

    const handleMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    return (
        <>
            <div className={listClasses.textfieldWithTooltip}>
                <TextField
                    variant="outlined"
                    id="input-with-icon-adornment"
                    data-cy="input-with-icon-adornment"
                    className={formsClasses.SearchField}
                    value={searchInput}
                    onChange={(e) => {
                        const { value } = e.target
                        setSearchInput(value)
                        debounceSearch(value)
                    }}
                    label={
                        <Typography className={formsClasses.SearchFieldLabel}>
                            <SearchRoundedIcon />
                            <span>
                                {isWorkbench
                                    ? 'Rechercher par nom(s) ou prénom(s)'
                                    : 'Rechercher par nom(s), prénom(s) ou INS'}
                            </span>
                        </Typography>
                    }
                    InputProps={{
                        endAdornment: !!filters.search && (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => {
                                        handleSearch('')
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <InformationalTooltip title="Recherche par nom(s), prénom(s), date de naissance ou matricule INS" />
            </div>
            <div className={classes.root}>
                <Button
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleMenu}
                    className={clsx(hasFilterActive && buttonClasses.ButtonActive, buttonClasses.ButtonTextBlackFlat)}
                    startIcon={<TuneRoundedIcon />}
                >
                    <Hidden smDown>Filtrer</Hidden>
                </Button>
                <Menu
                    id="menu-filters"
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    open={menuOpen}
                    onClose={handleClose}
                    className={classes.menu}
                >
                    <div className={formsClasses.FormContainer}>
                        <FormControl variant="standard" component="fieldset" className={formsClasses.FormControl}>
                            <FormGroup aria-label="position" className={formsClasses.FormGroup}>
                                <FormLabel component="legend">Médecin traitant</FormLabel>
                                <RadioGroup
                                    aria-label="referring expert status"
                                    name="referringExpertStatus"
                                    value={filters.attendingPhysicianStatus ?? null}
                                >
                                    {attendingPhysicianStatusOptions.map((option) => (
                                        <FormControlLabel
                                            key={option.value}
                                            control={
                                                <Radio
                                                    name="referringExpertStatus"
                                                    value={option.value}
                                                    onChange={() => handleAttendingPhysicianStatusChange(option.value)}
                                                />
                                            }
                                            label={option.label}
                                            labelPlacement="end"
                                        />
                                    ))}
                                </RadioGroup>
                            </FormGroup>
                        </FormControl>
                        {!isWorkbench && (
                            <FormControl variant="standard" component="fieldset" className={formsClasses.FormControl}>
                                <FormGroup aria-label="position" className={formsClasses.FormGroup}>
                                    <FormLabel component="legend">Statut INS</FormLabel>
                                    <RadioGroup
                                        aria-label="Insi identity status"
                                        name="InsiIdentityStatus"
                                        value={filters.insiIdentityStatuses ?? null}
                                    >
                                        {insiIdentityStatuses.map((status) => (
                                            <FormControlLabel
                                                key={status}
                                                control={
                                                    <Radio
                                                        name="InsiIdentityStatus"
                                                        value={status}
                                                        onChange={() => handleInsiStatusChange(status)}
                                                    />
                                                }
                                                label={getInsiIdentityStatusLabel(status)}
                                                labelPlacement="end"
                                            />
                                        ))}
                                    </RadioGroup>
                                </FormGroup>
                            </FormControl>
                        )}
                        <FormControl variant="standard" component="fieldset" className={formsClasses.FormControl}>
                            <FormGroup aria-label="position" className={formsClasses.FormGroup}>
                                <FormLabel component="legend">Date de naissance</FormLabel>
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                    localeText={frFR.components.MuiLocalizationProvider.defaultProps.localeText}
                                >
                                    <DatePicker
                                        slotProps={{
                                            textField: {
                                                id: 'birthDate',
                                                name: 'birthDate',
                                                error: false,
                                            },
                                        }}
                                        disableFuture
                                        format="DD/MM/YYYY"
                                        className={formsClasses.FormTextField}
                                        value={filters.birthDate ? dayjs.utc(filters.birthDate) : null}
                                        onChange={(date: Dayjs | null) => handleBirthDateChange(date)}
                                    />
                                </LocalizationProvider>
                            </FormGroup>
                        </FormControl>
                        {hasFilterActive && (
                            <div className={formsClasses.FormContainerBottom}>
                                <Button
                                    color="inherit"
                                    className={formsClasses.FormContainerBottomResetButton}
                                    onClick={() => handleResetFilter()}
                                    disabled={!hasFilterActive}
                                >
                                    Réinitialiser les filtres
                                </Button>
                            </div>
                        )}
                    </div>
                </Menu>
            </div>
        </>
    )
}

export default PatientListFilters
